// @ts-nocheck
/**
 * enumRenderer.ts — Template V2 M3 Enum Component Renderer
 *
 * Renders enum/category-picker components. Looks up categoryId and propertyId
 * from the compiledTemplate.categoriesById index to validate the reference,
 * emitting warnings for missing categories or properties (non-fatal: descriptor
 * is still produced so the preview surface can surface the issue visually).
 *
 * No React/RN imports; output is a plain serializable RenderDescriptor.
 */

import { componentInteractionProps } from './interactionProps';
import { applyExpressionFallback, buildDescriptor, resolveValue } from './rendererUtils';
import type { CompiledPropsByType, ComponentRenderer } from './types';
import { extractTypedProps } from './types';

// ── Enum ──────────────────────────────────────────────────────────────────────

export const ENUM_RENDERER: ComponentRenderer<CompiledPropsByType['enum']> = {
  supportedType: 'enum',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('enum', context.node.props);
    const value = resolveValue(p.enum.source, context);

    const { categoryId, propertyId } = p.enum;
    const category = context.compiledTemplate.categoriesById[categoryId];

    if (category === undefined) {
      context.issues.push({
        severity: 'warning',
        code: 'semantic.missing_category',
        message: `Enum renderer: category "${categoryId}" not found in compiled template.`,
        path: context.node.id,
      });
    } else {
      const property = category.properties.find((prop) => prop.id === propertyId);
      if (property === undefined) {
        context.issues.push({
          severity: 'warning',
          code: 'semantic.missing_property',
          message:
            `Enum renderer: property "${propertyId}" not found in category "${categoryId}".`,
          path: context.node.id,
        });
      }
    }

    const property = category?.properties.find((prop) => prop.id === propertyId);
    const enumValues = (property as { validation?: { enumValues?: Array<{ id: string; displayName?: import('../../types/metadata').LocalizedText }> } } | undefined)
      ?.validation?.enumValues;

    return buildDescriptor(context.node, visible, enabled, {
      value: value ?? null,
      categoryId,
      propertyId,
      editable: p.enum.editable ?? false,
      displayMode: p.enum.displayMode ?? 'label',
      enumValues: enumValues ?? [],
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};
